/* eslint new-cap: [0, { "newIsCapExceptions": ["iOS"] }] */

import $ from 'legacy/jquery';
import Android from './android';
import { addMobileEnvReadyCallback, setMobileEnvironmentFlag } from './env';
import iOS from './ios';
import MobileWeb from './mobile_web';
import {
  isRunningInsideAndroidWebView,
  isRunningInsideiOSWebView,
} from './runner_detector';
import UrlHelper from './url_helper';

/**
 * A WebView helper class that exposes specific information and does
 * specific manipulation of the DOM to be more friendly to
 * the native mobile devices that are using the
 * */
function WebViewHelper() {
  const self = this;
  self.mobileLibrary = null;

  /**
   * This is automatically called when {@link WebViewHelper#getInstance()} is used
   *
   * The method is exposed mostly for testing purposes
   * * */
  self.init = function () {
    setMobileEnvironmentFlag();
    self.mobileLibrary = self.mobileLibrary || self.getMobileLibrary();

    if (!self.isInitialized()) {
      // initialize and hand off the work to the appropriate library
      self.mobileLibrary.init();
    }
  };

  self.isInitialized = function () {
    return self.mobileLibrary !== null && self.mobileLibrary.isInitialized();
  };

  self.isRunningInsideWebView = function () {
    return isRunningInsideiOSWebView() || isRunningInsideAndroidWebView();
  };

  self.haveDeviceNavigateTo = function (url, title, isForNativeScreen) {
    if (self.mobileLibrary === null) {
      return;
    }

    return self.mobileLibrary.haveDeviceNavigateTo(
      url,
      title,
      isForNativeScreen
    );
  };

  self.haveDeviceDeeplinkTo = function (url, title, isForNativeScreen) {
    if (self.mobileLibrary === null) {
      return;
    }

    return self.mobileLibrary.haveDeviceDeeplinkTo(
      url,
      title,
      isForNativeScreen
    );
  };

  /**
   * The Native mobile apps expose both the auth token and the app agent string
   * to JS so it can be used for making ajax calls with the same signature
   * as the native apps.
   *
   * This method just takes those variables and reconstructs them into the same
   * header names and values that the native apps themselves use.
   *
   * @see mobile/webview/core.js
   * @see MobileHttpInterceptorService
   *
   * @return {Promise} - returns a promise that resolves to a headers object
   * */
  self.getEnvHeaders = function () {
    return new Promise(function (res, rej) {
      if (!self.isRunningInsideWebView()) {
        return res({});
      }

      self.tryGetHeadersWebView(res, rej);
    });
  };

  self.tryGetHeadersWebView = function (res, rej) {
    let tries = 0;

    let getAppHeaders = function () {
      if (tries >= 5) {
        $('#error_load').slideDown();
        return rej();
      }

      let headers = self.getAppEnvHeadersSync();

      if (headers.Authorization) {
        addMobileEnvReadyCallback(function onEnvHeadersRetrieval() {
          return headers;
        });

        return res(headers);
      } else {
        tries += 1;
        setTimeout(getAppHeaders, 100);
      }
    };

    getAppHeaders();
  };

  /**
   * A synchronous, cut down version of getEnvHeaders that assumes that
   * the window.Mobile.appEnv is already set.
   *
   * It is used internally by getEnvHeaders
   * */
  self.getAppEnvHeadersSync = function () {
    let headers = {};

    if (!self.isRunningInsideWebView()) {
      return headers;
    } else if (window.Mobile.appEnv) {
      headers.Authorization = window.Mobile.appEnv.authToken;
      headers['X-Greenhouse-Agent'] = window.Mobile.appEnv.appAgent;
    } else {
      // for easy debugging in local dev envs.

      /* eslint-disable */
      // the bind is for making sure console funcs are called with the context
      // of `console`. Otherwise, this throws an Illegal Invocation error
      let log =
        console && console.warn ? console.warn.bind(console) : function () {};
      /* eslint-enable */

      log(
        'The webViews have NOT properly set `authToken` or `appAgent`.' +
          'This could cause issues when making ajax calls!'
      );
    }

    return headers;
  };

  /**
   * Does a simple type check to see which library should be initialized
   *
   * This is done by the existence of certain objects.
   *
   *  - If its android, then the webview exposes the `window.Android` object
   *  - If its iOS, then the webview exposes the `window.webkit.messageHandlers` object
   * */
  self.getMobileLibrary = function () {
    if (isRunningInsideAndroidWebView()) {
      return new Android(new UrlHelper());
    } else if (isRunningInsideiOSWebView()) {
      return new iOS(new UrlHelper());
    }

    return new MobileWeb();
  };
}

let INSTANCE = null;

/**
 * To prevent multiple event handlers being binded or multiple instances
 * of this object to be created, it is exposed as a singleton instance instead
 * */
const WebViewHelperApi = {
  getInstance: function () {
    if (!INSTANCE) {
      INSTANCE = new WebViewHelper();
      INSTANCE.init();
    }

    return INSTANCE;
  },

  /**
   * Expose the actual type as well to be used ONLY FOR TESTING
   *
   * This is necessary to make sure that the full object can be reinitialized
   * and manipulated easily
   * */
  _WebViewHelper: WebViewHelper,
};

export default WebViewHelperApi;
